import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FORFEO_RESERVE_BOOKING_CHANNELS,
  FORFEO_RESERVE_CONFIRMATION_MODES,
  FORFEO_RESERVE_POLICY_EFFECTS,
  forfeoReservePolicyRequiresGiftshop,
  getForfeoReserveSelectedScopes,
  type ForfeoReserveBookingChannel,
  type ForfeoReserveCommercialPolicyContract,
  type ForfeoReserveConfirmationMode,
  type ForfeoReservePolicyEffect,
  type ForfeoReservePolicySnapshot,
} from '../../../supabase/functions/_shared/forfeo-reserve-policy';
import {
  applyForfeoReservePolicyCommand,
  createForfeoReservePolicyCommand,
  fetchForfeoReservePolicySnapshot,
} from '../../lib/forfeoReservePolicyClient';
import { humanizeForfeoReserveError } from '../../lib/forfeoReserveEdgeError';
import '../../styles/commerce-reserve-v21.css';

interface ReserveCommercialPoliciesProps {
  businessId: string;
  onUpdate?: () => void | Promise<void>;
}

interface PolicyDraft {
  label: string;
  confirmationMode: ForfeoReserveConfirmationMode;
  catalogItemId: string;
  resourceId: string;
  channel: ForfeoReserveBookingChannel;
  merchantPlanCode: string;
  cancellationWindowMinutes: number;
  modificationWindowMinutes: number;
  lateCancellationEffect: ForfeoReservePolicyEffect;
  noShowEffect: ForfeoReservePolicyEffect;
  financialPolicyReference: string;
  cancellationPolicyReference: string;
  creditPolicyReference: string;
  priority: number;
}

const initialDraft: PolicyDraft = {
  label: 'Politique générale',
  confirmationMode: 'free_booking',
  catalogItemId: '',
  resourceId: '',
  channel: 'any',
  merchantPlanCode: '',
  cancellationWindowMinutes: 1440,
  modificationWindowMinutes: 1440,
  lateCancellationEffect: 'merchant_review',
  noShowEffect: 'merchant_review',
  financialPolicyReference: '',
  cancellationPolicyReference: '',
  creditPolicyReference: '',
  priority: 100,
};

const modeLabels: Record<ForfeoReserveConfirmationMode, string> = {
  free_booking: 'Réservation gratuite',
  pay_on_site: 'Paiement sur place',
  deposit_required: 'Acompte requis',
  full_payment_required: 'Paiement complet requis',
  preauthorization_required: 'Préautorisation requise',
};

const effectLabels: Record<ForfeoReservePolicyEffect, string> = {
  none: 'Aucun effet financier',
  merchant_review: 'Révision marchande',
  giftshop_refund_review: 'Révision de remboursement Giftshop',
  giftshop_credit_review: 'Révision de crédit Giftshop',
  non_refundable_review: 'Révision non remboursable',
};

function safeMessage(error: unknown): string {
  return humanizeForfeoReserveError(
    error instanceof Error ? error : 'policy_operation_failed',
  );
}

export default function ReserveCommercialPolicies({
  businessId,
  onUpdate,
}: ReserveCommercialPoliciesProps) {
  const [snapshot, setSnapshot] = useState<ForfeoReservePolicySnapshot | null>(null);
  const [draft, setDraft] = useState<PolicyDraft>(initialDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setSnapshot(await fetchForfeoReservePolicySnapshot(businessId));
    } catch (reason) {
      setError(safeMessage(reason));
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  const policies = snapshot?.policies || [];
  const activeCount = useMemo(
    () => policies.filter((policy) => policy.state === 'active').length,
    [policies],
  );

  async function refresh(message: string) {
    await load();
    await onUpdate?.();
    setSuccess(message);
  }

  async function createPolicy(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const command = createForfeoReservePolicyCommand({
        kind: 'upsert_policy',
        businessId,
        policy: {
          label: draft.label.trim(),
          confirmationMode: draft.confirmationMode,
          selectors: {
            channel: draft.channel,
            ...(draft.catalogItemId.trim() ? { catalogItemId: draft.catalogItemId.trim() } : {}),
            ...(draft.resourceId.trim() ? { resourceId: draft.resourceId.trim() } : {}),
            ...(draft.merchantPlanCode.trim()
              ? { merchantPlanCode: draft.merchantPlanCode.trim() }
              : {}),
          },
          cancellationWindowMinutes: Number(draft.cancellationWindowMinutes),
          modificationWindowMinutes: Number(draft.modificationWindowMinutes),
          lateCancellationEffect: draft.lateCancellationEffect,
          noShowEffect: draft.noShowEffect,
          ...(draft.financialPolicyReference.trim()
            ? { financialPolicyReference: draft.financialPolicyReference.trim() }
            : {}),
          ...(draft.cancellationPolicyReference.trim()
            ? { cancellationPolicyReference: draft.cancellationPolicyReference.trim() }
            : {}),
          ...(draft.creditPolicyReference.trim()
            ? { creditPolicyReference: draft.creditPolicyReference.trim() }
            : {}),
          priority: Number(draft.priority),
        },
      });
      await applyForfeoReservePolicyCommand(command);
      setDraft(initialDraft);
      await refresh('Politique enregistrée en brouillon. Activez-la après vérification.');
    } catch (reason) {
      setError(safeMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  async function changeState(
    policy: ForfeoReserveCommercialPolicyContract,
    kind: 'activate_policy' | 'archive_policy',
  ) {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await applyForfeoReservePolicyCommand(createForfeoReservePolicyCommand({
        kind,
        businessId,
        policyId: policy.id,
        expectedRevision: policy.revision,
      }));
      await refresh(kind === 'activate_policy' ? 'Politique activée.' : 'Politique archivée.');
    } catch (reason) {
      setError(safeMessage(reason));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="reserve-policies-v21 reserve-policies-v213 reserve-policies-v214 mb-8 rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-7" data-commerce-panel="reserve-policies">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.24em] text-indigo-600">
            Forfeo Reserve · 4R-D
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-900">Politiques commerciales</h2>
          <p className="mt-2 max-w-3xl text-sm font-medium leading-6 text-slate-600">
            Reserve sélectionne et fige la règle applicable au moment du hold. Les montants,
            taxes, paiements, préautorisations, remboursements et crédits financiers restent
            exclusivement résolus par Giftshop.
          </p>
        </div>
        <div className="reserve-policies-v213__count rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-bold text-blue-800">
          {activeCount} politique{activeCount === 1 ? '' : 's'} active{activeCount === 1 ? '' : 's'}
        </div>
      </div>

      {error && (
        <div role="alert" className="reserve-policies-v21__error mt-5 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700 sm:flex-row sm:items-center sm:justify-between">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading || saving}
            className="shrink-0 rounded-xl border border-red-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-wider text-red-700 shadow-sm transition hover:bg-red-100 disabled:opacity-50"
          >
            Réessayer
          </button>
        </div>
      )}
      {success && (
        <div role="status" className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
          {success}
        </div>
      )}

      <form onSubmit={createPolicy} className="reserve-policies-v213__form mt-6 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          <label className="text-sm font-bold text-slate-700">
            Nom de la politique
            <input
              value={draft.label}
              onChange={(event) => setDraft({ ...draft, label: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
              maxLength={160}
              required
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Confirmation
            <select
              value={draft.confirmationMode}
              onChange={(event) => setDraft({
                ...draft,
                confirmationMode: event.target.value as ForfeoReserveConfirmationMode,
              })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            >
              {FORFEO_RESERVE_CONFIRMATION_MODES.map((mode) => (
                <option key={mode} value={mode}>{modeLabels[mode]}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold text-slate-700">
            Canal
            <select
              value={draft.channel}
              onChange={(event) => setDraft({
                ...draft,
                channel: event.target.value as ForfeoReserveBookingChannel,
              })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            >
              {FORFEO_RESERVE_BOOKING_CHANNELS.map((channel) => (
                <option key={channel} value={channel}>{channel}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold text-slate-700">
            ID service/catalogue (facultatif)
            <input
              value={draft.catalogItemId}
              onChange={(event) => setDraft({ ...draft, catalogItemId: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
              placeholder="UUID"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            ID ressource (facultatif)
            <input
              value={draft.resourceId}
              onChange={(event) => setDraft({ ...draft, resourceId: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
              placeholder="UUID"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Plan marchand (facultatif)
            <input
              value={draft.merchantPlanCode}
              onChange={(event) => setDraft({ ...draft, merchantPlanCode: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
              placeholder="prestige"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Annulation avant le début (minutes)
            <input
              type="number"
              min={0}
              max={525600}
              value={draft.cancellationWindowMinutes}
              onChange={(event) => setDraft({ ...draft, cancellationWindowMinutes: Number(event.target.value) })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Modification avant le début (minutes)
            <input
              type="number"
              min={0}
              max={525600}
              value={draft.modificationWindowMinutes}
              onChange={(event) => setDraft({ ...draft, modificationWindowMinutes: Number(event.target.value) })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Priorité
            <input
              type="number"
              min={0}
              max={100000}
              value={draft.priority}
              onChange={(event) => setDraft({ ...draft, priority: Number(event.target.value) })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Effet d’une annulation tardive
            <select
              value={draft.lateCancellationEffect}
              onChange={(event) => setDraft({
                ...draft,
                lateCancellationEffect: event.target.value as ForfeoReservePolicyEffect,
              })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            >
              {FORFEO_RESERVE_POLICY_EFFECTS.map((effect) => (
                <option key={effect} value={effect}>{effectLabels[effect]}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold text-slate-700">
            Effet d’une absence
            <select
              value={draft.noShowEffect}
              onChange={(event) => setDraft({
                ...draft,
                noShowEffect: event.target.value as ForfeoReservePolicyEffect,
              })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2"
            >
              {FORFEO_RESERVE_POLICY_EFFECTS.map((effect) => (
                <option key={effect} value={effect}>{effectLabels[effect]}</option>
              ))}
            </select>
          </label>
          <label className="text-sm font-bold text-slate-700">
            Référence financière Giftshop
            <input
              value={draft.financialPolicyReference}
              onChange={(event) => setDraft({ ...draft, financialPolicyReference: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
              placeholder={forfeoReservePolicyRequiresGiftshop(draft.confirmationMode)
                ? 'giftshop:policy:... (obligatoire)'
                : 'facultatif'}
              required={forfeoReservePolicyRequiresGiftshop(draft.confirmationMode)}
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Référence annulation Giftshop
            <input
              value={draft.cancellationPolicyReference}
              onChange={(event) => setDraft({ ...draft, cancellationPolicyReference: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
              placeholder="giftshop:cancellation:..."
            />
          </label>
          <label className="text-sm font-bold text-slate-700">
            Référence crédit Giftshop
            <input
              value={draft.creditPolicyReference}
              onChange={(event) => setDraft({ ...draft, creditPolicyReference: event.target.value })}
              className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 font-mono text-xs"
              placeholder="giftshop:credit:..."
            />
          </label>
        </div>
        <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs font-semibold text-slate-700">
            Aucun montant ni pourcentage n’est accepté. Les références pointent vers une configuration serveur Giftshop.
          </p>
          <button
            type="submit"
            disabled={saving}
            className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-black text-white shadow-sm hover:bg-blue-500 disabled:opacity-50"
          >
            {saving ? 'Enregistrement…' : 'Créer le brouillon'}
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-3">
        {loading ? (
          <div className="rounded-2xl border border-slate-200 p-5 text-sm font-bold text-slate-700">Chargement…</div>
        ) : policies.length === 0 ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm font-bold text-amber-800">
            Aucune politique active. Les confirmations gratuites resteront fermées jusqu’à l’activation d’une règle explicite.
          </div>
        ) : policies.map((policy) => (
          <article key={policy.id} className="rounded-2xl border border-slate-200 p-4 sm:p-5">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="font-black text-slate-900">{policy.label}</h3>
                  <span className={`rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-wider ${
                    policy.state === 'active'
                      ? 'bg-emerald-100 text-emerald-700'
                      : policy.state === 'draft'
                        ? 'bg-amber-100 text-amber-800'
                        : 'bg-slate-100 text-slate-600'
                  }`}>
                    {policy.state}
                  </span>
                </div>
                <p className="mt-2 text-sm font-bold text-indigo-700">{modeLabels[policy.confirmationMode]}</p>
                <p className="mt-2 text-xs font-semibold text-slate-700">
                  Portées : {getForfeoReserveSelectedScopes(policy.selectors).join(' · ')} · priorité {policy.priority} · révision {policy.revision}
                </p>
                <p className="mt-1 text-xs text-slate-700">
                  Annulation : {policy.cancellationWindowMinutes} min · modification : {policy.modificationWindowMinutes} min
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {policy.state === 'draft' && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void changeState(policy, 'activate_policy')}
                    className="rounded-xl bg-emerald-600 px-4 py-2 text-xs font-black text-white disabled:opacity-50"
                  >
                    Activer
                  </button>
                )}
                {policy.state !== 'archived' && (
                  <button
                    type="button"
                    disabled={saving}
                    onClick={() => void changeState(policy, 'archive_policy')}
                    className="rounded-xl bg-slate-200 px-4 py-2 text-xs font-black text-slate-700 disabled:opacity-50"
                  >
                    Archiver
                  </button>
                )}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
